/**
 * CompletionsSurface — SPD v5.87 · Predictive Completions inline UI.
 *
 * Sits directly below the QueryInputRow textarea and above the
 * typeahead-chip dropdown. Presents 0-3 predictive completions
 * inspired by Google's autocomplete behaviour, but with the crucial
 * refinement-awareness the operator flagged as missing on Google.
 *
 * Design language (matches the operator-approved wireframe at
 * /mockups/unified-thread.html):
 *   • Small uppercase "LIVE SUGGESTIONS" label with a monospace
 *     latency badge that shows the last render time in milliseconds.
 *     Doubles as an in-app proof that the sub-100 ms guarantee is
 *     being kept.
 *   • Each completion renders as plain cyan text with a dashed hover
 *     underline. The prefix the user has typed is bolded so they can
 *     see where their input stops and the AI-added suffix begins
 *     (same trick as Google Autocomplete).
 *   • When the current partial is detected as a refinement of a
 *     recent query, a tiny cyan pill hints "refining ${prevQuery}"
 *     so the user knows the completions are biased by that context.
 *
 * Interaction contract
 *   • Click a completion → parent's `onPick(text)` fires. Parent
 *     typically routes through `attemptProductSearch` (compare
 *     default) or `handleQuery` per detected intent.
 *   • Keyboard nav (up/down/tab/enter) is owned by the parent
 *     textarea; this component is presentational.
 */
import React from 'react';

const highlightPrefix = (completion, prefix) => {
  const p = String(prefix || '').trim().toLowerCase();
  if (!p) return completion;
  const idx = completion.toLowerCase().indexOf(p);
  if (idx < 0) return completion;
  return (
    <>
      {completion.slice(0, idx)}
      <span style={{ color: '#a5f3fc', fontWeight: 600 }}>
        {completion.slice(idx, idx + p.length)}
      </span>
      {completion.slice(idx + p.length)}
    </>
  );
};

export const CompletionsSurface = ({
  state,       // { completions, isRefinement, anchoredTo, partial, tookMs }
  onPick,      // (text: string) => void
  dataTestId = 'completions-surface',
}) => {
  if (!state || !state.partial) return null;
  const { completions = [] } = state;
  if (completions.length === 0) return null;

  return (
    <div
      className="mt-1 pl-1"
      style={{ maxWidth: '720px' }}
      data-testid={dataTestId}
    >
      <div>
        {completions.map((c, i) => (
          <button
            key={`${c.text}-${i}`}
            type="button"
            onClick={() => onPick(c.text)}
            className="block text-left transition-all"
            style={{
              fontSize: '18px',
              color: '#22d3ee',
              cursor: 'pointer',
              padding: '4px 0',
              borderBottom: '1px dashed transparent',
              background: 'transparent',
              border: 'none',
              width: 'fit-content',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderBottomColor = '#22d3ee'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderBottomColor = 'transparent'; }}
            data-testid={`${dataTestId}-item-${i}`}
          >
            {highlightPrefix(c.text, state.partial)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CompletionsSurface;
